import React, { useState, useCallback, useEffect } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Theme,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { usePermission } from '@backstage/plugin-permission-react';
import { openControlReprocessPermission } from '@internal/backstage-plugin-supply-features-common';
import { requestManagerCreatePermission } from '@internal/backstage-plugin-request-manager-common';
import { TotvsLogoIcon } from '../../themes';
import LinkAddonDialog from './LinkAddonDialog';
import { systemAddonsServiceApiRef } from '../../apis';
import { UpdateInsightsTablesDialog } from './UpdateInsightsTablesDialog';
import { NotificationSnackbar } from '../notification-snackbar/notification-snackbar';
import { useNotification } from '../../hooks/useNotification';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  productionChip: {
    margin: 0,
    marginLeft: '1em',
    letterSpacing: 0.75,
  },
  divider: { marginTop: '1em' },
}));

interface Addon {
  appCode: string;
  title: string;
  description?: string;
}

type Environment = 'development' | 'staging' | 'production';

interface AddonsTabProps {
  owner: string;
}

const AddonItem = ({ 
  addon, 
  canManageAddons, 
  handleUnlink, 
  handleUpdateTables,
}: { 
  addon: Addon; 
  canManageAddons: boolean; 
  handleUnlink: (appCode: string) => void; 
  handleUpdateTables: () => void;
}) => {
  const classes = useStyles();
  const { allowed: canReprocessTables } = usePermission({ 
    permission: openControlReprocessPermission 
  });
  
  return (
    <ListItem key={addon.appCode} className={classes.root}>
      <ListItemIcon>
        <TotvsLogoIcon />
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="body2">
            {addon.title}
            <Chip
              component="span"
              label={addon.appCode}
              size="small"
              className={classes.productionChip}
            />
          </Typography>
        }
        secondary={addon.description}
      />
      {addon.appCode === 'add001' && (
        <Tooltip title={!canReprocessTables ? "Only users with techlead role can perform this action." : ""}>
          <span>
            <Button
              onClick={handleUpdateTables}
              color="primary"
              variant="contained"
              style={{ marginRight: 24 }}
              disabled={!canReprocessTables}
            >
              Notify changes on tables
            </Button>
          </span>
        </Tooltip>
      )}
      {canManageAddons && (
        <IconButton
          edge="end"
          aria-label="unlink"
          size="small"
          onClick={() => handleUnlink(addon.appCode)}
        >
          <ClearIcon />
        </IconButton>
      )}
    </ListItem>
  );
};

export const AddonsTab = ({ owner }: AddonsTabProps) => {
  const catalogApi = useApi(catalogApiRef);
  const systemAddonsService = useApi(systemAddonsServiceApiRef);
  const classes = useStyles();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [updateTablesDialogOpen, setUpdateTablesDialogOpen] = useState(false);
  const [currentAddons, setCurrentAddons] = useState<string[]>([]);
  const [addonsDetails, setAddonsDetails] = useState<Addon[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [entity, setEntity] = useState<Entity | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  
  const { notification, showSuccess, hideNotification } = useNotification();
  const { allowed: canManageAddons } = usePermission({
    permission: requestManagerCreatePermission,
  });

  const fetchEntity = useCallback(async () => {
    try {
      const cleanOwner = owner.replace(/^system:/, '');
      const nameParts = cleanOwner.split('/');
      const systemName = nameParts[nameParts.length - 1];

      const response = await catalogApi.queryEntities({
        filter: {
          kind: 'System',
          'metadata.name': systemName,
        },
      });

      if (response.items.length > 0) {
        const fetchedEntity = response.items[0] as Entity;
        setEntity(fetchedEntity);
        setError(undefined);
        return fetchedEntity;
      }

      setError(`System "${systemName}" not found`);
      return undefined;
    } catch (err) {
      setError('Failed to fetch system');
      return undefined;
    }
  }, [owner, catalogApi]);

  const getEnvironment = useCallback((): Environment => {
    if (!entity?.metadata?.annotations) return 'production';
    const env = entity.metadata.annotations['open.totvs.io/environment'];
    return (env as Environment) || 'production';
  }, [entity]);

  const extractAddonsFromAnnotation = useCallback((addonEntity: Entity): string[] => {
    const addonsAnnotation = addonEntity?.metadata?.annotations?.['open.totvs.io/addons'];
    if (!addonsAnnotation) return [];

    try {
      const parsed = JSON.parse(addonsAnnotation);
      if (Array.isArray(parsed)) return parsed;
      return [];
    } catch {
      return addonsAnnotation
        .split(',')
        .map(addon => addon.trim())
        .filter(Boolean);
    }
  }, []);

  const loadAddons = useCallback(async () => {
    setLoading(true);
    try {
      const currentEntity = entity || (await fetchEntity());

      if (!currentEntity) {
        setLoading(false);
        return;
      }

      const addonCodes = extractAddonsFromAnnotation(currentEntity);
      setCurrentAddons(addonCodes);

      if (addonCodes.length === 0) {
        setAddonsDetails([]);
        return;
      }

      const response = await catalogApi.queryEntities({
        filter: {
          kind: ['System'],
          'metadata.annotations.open.totvs.io/addon-system': 'true',
        },
        fields: [
          'metadata.title',
          'metadata.name',
          'metadata.description',
          'metadata.annotations',
        ],
      });

      const details = response.items
        .map(item => ({
          appCode: item.metadata?.annotations?.['open.totvs.io/app-code'] || '',
          title: item.metadata?.title || item.metadata?.name || '',
          description: item.metadata?.description,
        }))
        .filter(addon => addonCodes.includes(addon.appCode)) as Addon[];

      setAddonsDetails(details);
    } catch (err) {
      console.error('Failed to load addons:', err);
      setAddonsDetails([]);
    } finally {
      setLoading(false);
    }
  }, [entity, fetchEntity, catalogApi, extractAddonsFromAnnotation]);

  useEffect(() => {
    loadAddons();
  }, [loadAddons]);

  const updateEntityAddons = useCallback(
    async (updatedAddons: string[], insightsResources?: string[]) => {
      if (!entity) {
        throw new Error('Entity is not available');
      }

      setUpdating(true);
      try {
        const systemRef = stringifyEntityRef(entity);
        const environment = getEnvironment();

        await systemAddonsService.updateSystemAddons(
          environment,
          systemRef,
          updatedAddons,
          insightsResources,
        );

        setCurrentAddons(updatedAddons);
        await catalogApi.refreshEntity(systemRef);
        await fetchEntity();
        await loadAddons();
      } catch (err) {
        console.error('Failed to update addons:', err);
        throw err;
      } finally {
        setUpdating(false);
      }
    },
    [entity, getEnvironment, systemAddonsService, catalogApi, fetchEntity, loadAddons],
  );

  const handleLink = useCallback(
    async (selectedAddons: string[], selectedResources?: string[]) => {
      try {
        const updatedAddons = [...new Set([...currentAddons, ...selectedAddons])];

        await updateEntityAddons(updatedAddons, selectedResources);
      } catch (err) {
        console.error('Failed to link addons:', err);
      }
    },
    [currentAddons, updateEntityAddons],
  );

  const handleUnlink = useCallback(
    async (appCode: string) => {
      try {
        const updatedAddons = currentAddons.filter(code => code !== appCode);
        await updateEntityAddons(updatedAddons);
      } catch (err) {
        console.error('Failed to unlink addon:', err);
      }
    },
    [currentAddons, updateEntityAddons],
  );

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleDialogOpen = useCallback(() => {
    setDialogOpen(true);
  }, []);

  if (!loading && error) {
    return (
      <Box mt={2}>
        <Typography align="center" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (loading || updating) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {canManageAddons && (
        <>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Button
              variant="contained"
              color="secondary"
              onClick={handleDialogOpen}
              disabled={!entity}
            >
              Link Addon
            </Button>
          </Box>
          <Divider className={classes.divider} />
        </>
      )}

      {addonsDetails.length === 0 ? (
        <Box mt={2}>
          <Typography align="center">No addons linked.</Typography>
        </Box>
      ) : (
        <List dense>
          {addonsDetails.map(addon => (
            <AddonItem 
              key={addon.appCode} 
              addon={addon} 
              canManageAddons={canManageAddons} 
              handleUnlink={handleUnlink} 
              handleUpdateTables={() => setUpdateTablesDialogOpen(true)}
            />
          ))}
        </List>
      )}

      {canManageAddons && (
        <LinkAddonDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          onLink={handleLink}
          currentAddons={currentAddons}
          systemOwner={owner}
        />
      )}

      <UpdateInsightsTablesDialog 
        open={updateTablesDialogOpen}
        systemEntity={entity}
        onClose={(message: string) => {
          setUpdateTablesDialogOpen(false);
          if (message) {
            showSuccess(message);
          }
        }}
      />

      <NotificationSnackbar notification={notification} onClose={hideNotification} />
    </>
  );
};
